import { useEffect } from "react";
import { navigate, useParams } from "@reach/router";
import { useProject } from "../../hooks/useProject";
import { useTeam } from "../../hooks/useTeam";

import { api } from "../../util/api";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { Materials } from "./ProjectMaterials";
import { ClientFormAddress } from "./ClientFormAddress";
import { Lengths } from "./Lengths";
import { DesignPicker } from "./DesignPicker";
import { ProjectNumbers } from "./ProjectNumbers";
import { Notes } from "./Notes";
import { ButtonTW } from "../../comp/tw/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ModalTW } from "../../comp/tw/Modal";
import SelectTW from "../../comp/tw/Select";
import { ChainlinkDesignPicker } from "./ChainlinkDesignPicker";
import ChainlinkLogic from "./ChainlinkLogic";
import BlankLogic from "./BlankLogic";

const Project = () => {
  const { projectId } = useParams();
  const { project, isLoading, error, mutate } = useProject(projectId);
  const [areYouSure, setAreYouSure] = useState(false);

  const { team, isLoading: teamIsLoading, error: teamError } = useTeam();

  const changeType = async (e) => {
    const newType = e.target.value;
    await mutate(
      api("PUT", `/project/${project.id}`, {
        type: newType,
      }),
      {
        optimisticData: {
          ...project,
          type: newType,
        },
      },
    );
  };

  useEffect(() => {
    if (
      project?.polylines &&
      project.polylines[0] &&
      project.polylines[0]["lng"]
    ) {
      console.log("FIX PROJECT");
      const newProjectPoly = [[...project.polylines]];
      mutate(
        api("PUT", `/project/${project.id}`, {
          polylines: newProjectPoly,
        }),
        {
          optimisticData: {
            ...project,
            polylines: newProjectPoly,
          },
        },
      );
    }
  }, [mutate, project]);

  useEffect(() => {
    if (project && !project.type) {
      console.log("Setting project type to traditional");
      mutate(
        api("PUT", `/project/${project.id}`, {
          type: "traditional",
        }),
        {
          optimisticData: {
            ...project,
            type: "traditional",
          },
        },
      );
    }
  }, [mutate, project]);

  if (isLoading || teamIsLoading) {
    return <div className="spinner" />;
  }

  if (error || teamError) {
    return (
      <p>
        Error! please refresh the page, if the problem persists contact
        fencewizapp@gmail.com
      </p>
    );
  }

  const deleteButton = async () => {
    enqueueSnackbar("Deleting Project...");
    await api("DELETE", `/project/${project.id}`);
    enqueueSnackbar("Deleted", { variant: "success" });
    navigate(`/`);
  };

  const copyProject = async () => {
    enqueueSnackbar("Copying Project...");
    // Create new project
    const newProject = await api("POST", `/project`);

    // Copy over all the relevant data from current project
    const dataToCopy = {
      clientFirstName: project.clientFirstName,
      clientLastName: project.clientLastName,
      clientEmail: project.clientEmail,
      clientPhone: project.clientPhone,
      address: project.address,
      city: project.city,
      state: project.state,
      country: project.country,
      lengths: project.lengths,
      designId: project.designId,
      chainlinkDesignId: project.chainlinkDesignId,
      sharedPosts: project.sharedPosts,
      polylines: project.polylines,
      position: project.position,
      mapCenter: project.mapCenter,
      extras: project.extras,
      notes: project.notes ? `Copy of: ${project.notes}` : "Copy of project",
      adjustCost: project.adjustCost,
      splits: project.splits,
      type: project.type,
      chainlinkSingleGates: project.chainlinkSingleGates,
      chainlinkDoubleGates: project.chainlinkDoubleGates,
      chainlinkGateExtensions: project.chainlinkGateExtensions,
      chainlinkGatePosts: project.chainlinkGatePosts,
    };

    // Update the new project with copied data
    await api("PUT", `/project/${newProject.id}`, dataToCopy);

    enqueueSnackbar("Project Copied!", { variant: "success" });
    navigate(`/project/${newProject.id}`);
  };

  return (
    <>
      <div>
        <h1 className="text-4xl font-bold mb-4">Project</h1>
        <ClientFormAddress />
        <br />

        <div className="mv-2">
          <SelectTW
            label={"Project Type"}
            value={project.type}
            onChange={changeType}
            selectCategories={["traditional", "chainlink", "blank"]}
          />
        </div>
        <br />

        <Notes />

        <Lengths project={project} mutate={mutate} />

        {project.type === "traditional" ? (
          <>
            <DesignPicker />

            {project.designId ? (
              <>
                <Materials />
                <ProjectNumbers />
              </>
            ) : null}
          </>
        ) : null}

        {project.type === "chainlink" ? (
          <>
            <ChainlinkDesignPicker />

            {project.chainlinkDesignId ? (
              <>
                <ChainlinkLogic />
              </>
            ) : null}
          </>
        ) : null}

        {project.type === "blank" ? (
          <>
            <BlankLogic />
          </>
        ) : null}

        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />

        <div className="flex gap-2">
          {team.permissions.includes("createProject") ? (
            <ButtonTW
              variant="secondary"
              onClick={copyProject}
              label={<ContentCopyIcon />}
            >
              Copy Project
            </ButtonTW>
          ) : null}

          {team.permissions.includes("deleteProject") ? (
            <ButtonTW
              variant="danger"
              color="error"
              onClick={() => setAreYouSure(true)}
              label={<DeleteIcon />}
            >
              Delete
            </ButtonTW>
          ) : null}
        </div>

        <ModalTW
          isOpen={areYouSure}
          onClose={() => setAreYouSure(false)}
          text="Are you sure you want to delete the project?"
          confirm={deleteButton}
        />
      </div>
    </>
  );
};

export default Project;
